import { Suspense, useCallback } from "react";
import type { PropsWithChildren } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { ProductKeys } from "@atlassiansox/asc-stargate";

import { CrossFlowProvider } from "@/components/cross-flow";
import { CrossFlowTouchpointProvider } from "@/components/cross-flow/touchpoint";
import ErrorBoundary from "@/components/error-boundary";
import type { Template } from "@/content/templates/template.types";
import { TypographyStyles } from "@/libraries/wpl/foundations/src";
import { SubNavJiraTemplates } from "@/patterns/sub-nav/src/subnav-jira-templates";
import { LoadingSpinner } from "@/templates/software/rovo/start/software-rovo-start-page/src/components/spinner";
import { experiments, useExperiment } from "@/utils/experiment";
import {
    checkFeatureFlagGate,
    featureFlags
} from "@/utils/feature-flags/index.ts";

import { JSM_TEMPLATES_CONTENT } from "./content/templates-grid-content";
import { Resources } from "./sections/resources";
import { SharedHeaderFooterContainer } from "./sections/shared-header-footer-container";
import { TemplatesGrid } from "./sections/templates-grid";
export type JiraTemplateCategoryPageTemplateProps = PropsWithChildren<{
    templates?: Template[];
}>;

type ExperimentCohortType =
    | "control"
    | "variation"
    | "indeterminate"
    | "notEnrolled";

const PageContent = ({
    cohort,
    templates
}: {
    cohort: ExperimentCohortType;
    templates?: Template[];
}) => {
    const templateCategoriesHidden = checkFeatureFlagGate(
        featureFlags.jiraTemplatesDxpMigration
    );
    return (
        <SharedHeaderFooterContainer>
            <TypographyStyles>
                {!templateCategoriesHidden && <SubNavJiraTemplates />}
                <Suspense>
                    {cohort === "indeterminate" ? (
                        <LoadingSpinner />
                    ) : (
                        <TemplatesGrid
                            templates={templates ?? JSM_TEMPLATES_CONTENT}
                            templatesTabInHeader={cohort === "variation"}
                        />
                    )}
                </Suspense>
                <Resources />
            </TypographyStyles>
        </SharedHeaderFooterContainer>
    );
};

export const JiraTemplateCategoryPageTemplate = ({
    templates
}: JiraTemplateCategoryPageTemplateProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const experimentStatus = useExperiment({
        ...experiments.templatesTabInHeader,
        options: {
            fireExperimentExposure: false,
            typeGuard: experiments.templatesTabInHeader.options.typeGuard
        }
    });

    const experimentCohort: ExperimentCohortType =
        experimentStatus.type === "enrolled"
            ? experimentStatus.value
            : experimentStatus.type;

    const fireSLOErrorEvent = useCallback(
        (error: unknown) => {
            createAnalyticsEvent({
                product: "marketing",
                eventType: "operational",
                actionSubject: "wacJsmTemplateScreenFailed",
                action: "failed",
                attributes: {
                    errorMessage: (error as Error)?.message || error,
                    templatesTabInHeaderCohort: experimentCohort
                }
            }).fire();
        },
        [createAnalyticsEvent, experimentCohort]
    );

    return (
        <ErrorBoundary
            fallback={<PageContent cohort="control" templates={templates} />}
            onError={(e) => fireSLOErrorEvent(e)}
        >
            <CrossFlowProvider>
                <CrossFlowTouchpointProvider
                    productKeys={[ProductKeys.JIRA_SERVICE_DESK]}
                    screenName="wacJsmTemplateScreen"
                >
                    <PageContent
                        cohort={experimentCohort}
                        templates={templates}
                    />
                </CrossFlowTouchpointProvider>
            </CrossFlowProvider>
        </ErrorBoundary>
    );
};
